import { useState } from 'react';
import Alert from '@material-ui/lab/Alert';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import TextField from '@material-ui/core/TextField';
import useCreateFeedback from '../hooks/useCreateFeedback';

const MAX_MESSAGE_LENGTH = 1500;

export default function SuggestionBoxDialog({ open, handleClose }) {
  const [message, setMessage] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const createFeedback = useCreateFeedback();

  const resetForm = () => {
    setMessage('');
    setIsAnonymous(false);
    createFeedback.reset();
  };

  const handleSubmit = async () => {
    if (!message.trim() || createFeedback.isLoading) return;

    try {
      await createFeedback.mutateAsync({
        message,
        isAnonymous,
      });
      handleClose();
    } catch (error) {
      // The mutation hook displays the error to the user.
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      onExited={resetForm}
      disableBackdropClick={createFeedback.isLoading}
      disableEscapeKeyDown={createFeedback.isLoading}
      maxWidth="sm"
      fullWidth
      aria-labelledby="suggestion-box-dialog-title"
    >
      <DialogTitle id="suggestion-box-dialog-title">
        Suggestion Box
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" style={{ marginBottom: 16 }}>
          Share suggestions, improvements, feature requests or things you find
          difficult to use. Your feedback helps us improve
          Traciwr.
        </Alert>
        <TextField
          autoFocus
          id="suggestion-box-message"
          label="Your suggestion or feedback"
          placeholder="Tell us what you would like to see improved..."
          multiline
          rows={6}
          variant="outlined"
          fullWidth
          required
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          disabled={createFeedback.isLoading}
          inputProps={{ maxLength: MAX_MESSAGE_LENGTH }}
          helperText={`${message.length}/${MAX_MESSAGE_LENGTH} characters`}
        />
        <FormControlLabel
          style={{ marginTop: 8 }}
          control={
            <Checkbox
              checked={isAnonymous}
              onChange={(event) => setIsAnonymous(event.target.checked)}
              color="primary"
              disabled={createFeedback.isLoading}
            />
          }
          label="Submit anonymously"
        />
        {isAnonymous && (
          <Alert severity="info" style={{ marginTop: 4 }}>
            Your name and email will not be included. Your school will still be
            recorded, and we will not be able to contact you about your
            feedback.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="primary"
          disabled={createFeedback.isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={createFeedback.isLoading || !message.trim()}
          startIcon={
            createFeedback.isLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : null
          }
        >
          {createFeedback.isLoading ? 'Sending...' : 'Send feedback'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
